/**
 * Error Handler Utility
 * 
 * 統一的錯誤處理工具
 * 提供錯誤分類、格式化、用戶友好消息等功能
 */

import logger from './logger';

/**
 * 錯誤類型枚舉
 */
export const ErrorTypes = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  CORS_ERROR: 'CORS_ERROR',
  API_ERROR: 'API_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  STORAGE_ERROR: 'STORAGE_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
};

/**
 * 錯誤分類
 * @param {Error} error - 錯誤對象
 * @returns {string} 錯誤類型
 */
export const classifyError = (error) => {
  if (!error) {
    return ErrorTypes.UNKNOWN_ERROR;
  }

  // 網絡錯誤
  if (
    error.code === 'ERR_NETWORK' ||
    error.message?.includes('Network Error') ||
    error.message?.includes('Failed to fetch') ||
    error.message?.includes('Network request failed')
  ) {
    return ErrorTypes.NETWORK_ERROR;
  }

  // CORS 錯誤
  if (
    error.message?.includes('CORS') ||
    error.message?.includes('Access-Control-Allow-Origin') ||
    error.message?.includes('Cross-Origin')
  ) {
    return ErrorTypes.CORS_ERROR;
  }

  // API 錯誤（HTTP 狀態碼錯誤）
  if (error.response || error.status) {
    return ErrorTypes.API_ERROR;
  }

  // Storage 錯誤
  if (
    error.name === 'QuotaExceededError' ||
    error.message?.includes('localStorage') ||
    error.message?.includes('sessionStorage')
  ) {
    return ErrorTypes.STORAGE_ERROR;
  }

  return ErrorTypes.UNKNOWN_ERROR;
};

/**
 * 格式化錯誤消息
 * @param {Error} error - 錯誤對象
 * @param {string} language - 語言 ('tc' 或 'en')
 * @returns {string} 格式化的錯誤消息
 */
export const formatErrorMessage = (error, language = 'tc') => {
  const errorType = classifyError(error);
  const messages = {
    tc: {
      [ErrorTypes.NETWORK_ERROR]: '網絡錯誤，請檢查網絡連接。',
      [ErrorTypes.CORS_ERROR]: 'CORS 錯誤，請檢查瀏覽器設置或網絡連接。',
      [ErrorTypes.API_ERROR]: 'API 錯誤，請稍後再試。',
      [ErrorTypes.VALIDATION_ERROR]: '數據驗證錯誤。',
      [ErrorTypes.STORAGE_ERROR]: '存儲錯誤，請檢查瀏覽器設置。',
      [ErrorTypes.UNKNOWN_ERROR]: '發生未知錯誤，請稍後再試。',
    },
    en: {
      [ErrorTypes.NETWORK_ERROR]: 'Network error. Please check your network connection.',
      [ErrorTypes.CORS_ERROR]: 'CORS error. Please check browser settings or network connection.',
      [ErrorTypes.API_ERROR]: 'API error. Please try again later.',
      [ErrorTypes.VALIDATION_ERROR]: 'Data validation error.',
      [ErrorTypes.STORAGE_ERROR]: 'Storage error. Please check browser settings.',
      [ErrorTypes.UNKNOWN_ERROR]: 'An unknown error occurred. Please try again later.',
    },
  };

  return messages[language]?.[errorType] || messages[language]?.[ErrorTypes.UNKNOWN_ERROR];
};

/**
 * 處理錯誤
 * @param {Error} error - 錯誤對象
 * @param {string} context - 錯誤上下文（可選）
 * @param {string} language - 語言 ('tc' 或 'en')
 * @returns {object} 錯誤信息對象
 */
export const handleError = (error, context = '', language = 'tc') => {
  const errorType = classifyError(error);
  const message = formatErrorMessage(error, language);

  // 記錄錯誤
  if (context) {
    logger.error(`[${context}]`, error);
  } else {
    logger.error(error);
  }

  // 發送錯誤到 Sentry（如果已配置且是生產環境）
  if (process.env.REACT_APP_SENTRY_DSN && process.env.NODE_ENV === 'production') {
    import('@sentry/react').then((Sentry) => {
      Sentry.captureException(error, {
        tags: {
          errorType,
          context: context || 'unknown',
        },
        extra: {
          message,
          language,
        },
      });
    }).catch((err) => {
      console.warn('Failed to send error to Sentry:', err);
    });
  }

  return {
    errorType,
    message,
    originalError: error,
    context,
  };
};

/**
 * 創建錯誤對象
 * @param {string} errorType - 錯誤類型
 * @param {string} message - 錯誤消息
 * @param {Error} originalError - 原始錯誤（可選）
 * @returns {object} 錯誤對象
 */
export const createError = (errorType, message, originalError = null) => {
  return {
    errorType,
    message,
    originalError,
    timestamp: new Date().toISOString(),
  };
};

